import { ImagePaths } from '../../lib/imageUrls.js';

export const smbIncidentResponsePlaybook = {

    id: 15,
    slug: 'smb-incident-response-playbook',
    ogImage: '/og/courses/smb-incident-response-playbook.jpg',
    courseCode: 'SMB-IR-001',
    title: 'SMB Incident Response Playbook: What to Do When an Attack Happens',
    subtitle: 'Actionable incident response for small and medium businesses',
    valueProposition: 'Gain confidence to respond effectively to cyber incidents with practical templates, step-by-step playbooks, and clear communication strategies tailored for SMB realities.',
    duration: '16 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_2,
    shortDescription: 'SMB-IR-001 — Incident response playbook covering detection, containment, recovery, and NDB notification for Australian SMB owners and IT staff.',
    price: {
      aud: 1250,
      currency: 'AUD',
      earlyBird: 999
    },
    stripeConfig: {
    priceId: 'price_1T9bE7Ku1nyc322UfbmiTEwb',
    productId: 'prod_U7qvxFJCkHudvh'
  },
    
    snapshot: {
      whoItsFor: [
        'SMB owners and key decision-makers',
        'IT staff in small businesses',
        'Managers responsible for business continuity',
        'Anyone overseeing incident response planning'
      ],
      accreditation: 'Aligned to ACSC Cyber Incident Response Plan guidance and Essential Eight'
    },
    
    logistics: {
      classSize: 'Maximum 20 participants for hands-on exercises',
      requirements: [
        'Basic understanding of cybersecurity concepts (or completion of SMB Cybersecurity Essentials)',
        'Computer with internet access',
        'Authority to implement incident response procedures',
        'Access to your organisation\'s systems and contacts (for planning exercises)'
      ]
    },
    
    overview: 'This practical 16-hour course prepares SMB owners, IT staff, and decision-makers to respond confidently and effectively when a cyber incident occurs. You\'ll learn simple, actionable steps for detecting, containing, recovering from, and communicating about security incidents—without complex forensic analysis or enterprise-grade tools. Through template-driven exercises, you\'ll build your organisation\'s customised incident response playbook covering ransomware, data breaches, phishing compromises, and system intrusions. The course emphasises real-world SMB scenarios including notification obligations under Australian privacy law, working with external responders, managing reputation, and recovering operations quickly with limited resources.',
    
    outcomes: [
      'Detect and identify common cyber incidents affecting SMBs',
      'Execute immediate containment actions to limit incident spread',
      'Follow step-by-step response procedures for ransomware, data breaches, and system compromises',
      'Communicate effectively with employees, customers, authorities, and media during incidents',
      'Understand Australian notification obligations under the Privacy Act and Notifiable Data Breaches scheme',
      'Coordinate with external incident responders, IT providers, and law enforcement',
      'Execute data recovery and business continuity procedures',
      'Conduct post-incident reviews and implement lessons learned'
    ],
    
    audience: [
      'SMB owners and directors',
      'IT staff and system administrators in small businesses',
      'Business continuity and operations managers',
      'Anyone responsible for incident response decisions',
      'External IT consultants supporting SMB clients',
      'Business managers overseeing risk and compliance'
    ],
    
    prerequisites: [
      'Basic understanding of cybersecurity threats and defences',
      'Recommended: Completion of SMB Cybersecurity Essentials or equivalent knowledge',
      'Familiarity with your organisation\'s systems and operations',
      'Authority to make incident response decisions'
    ],
    
    modules: [
      {
        title: 'Incident Detection and Classification',
        duration: '2.5 hours',
        topics: [
          'Recognising signs of a security incident',
          'Common incident types: ransomware, data breach, phishing compromise, system intrusion',
          'Severity classification for SMB incidents',
          'When to escalate and seek external help',
          'Initial assessment and scoping',
          'Logging and evidence preservation basics'
        ]
      },
      {
        title: 'Immediate Containment Actions',
        duration: '3 hours',
        topics: [
          'Isolating affected systems safely',
          'Stopping the spread of ransomware and malware',
          'Disabling compromised accounts and credentials',
          'Securing backup systems and data',
          'Network segmentation for SMBs',
          'Making critical containment decisions under pressure',
          'Documenting containment actions'
        ]
      },
      {
        title: 'Communication and Notification',
        duration: '3 hours',
        topics: [
          'Internal communication during incidents',
          'Notifying affected customers and stakeholders',
          'Australian Notifiable Data Breaches (NDB) scheme requirements',
          'When and how to notify the Office of the Australian Information Commissioner (OAIC)',
          'Working with law enforcement (Australian Federal Police, state police)',
          'Media and public relations considerations',
          'Template communications and notification letters'
        ]
      },
      {
        title: 'Recovery and Business Continuity',
        duration: '3.5 hours',
        topics: [
          'Data recovery from backups',
          'Rebuilding compromised systems safely',
          'Business continuity strategies with limited resources',
          'Prioritising recovery based on business impact',
          'Testing recovered systems before returning to operation',
          'Managing downtime and alternative work arrangements',
          'Should you pay a ransom? Considerations and guidance'
        ]
      },
      {
        title: 'Working with External Responders',
        duration: '2 hours',
        topics: [
          'When to engage external incident response services',
          'Working effectively with IT providers and MSPs',
          'Engaging cyber insurance and understanding coverage',
          'Coordinating with the ACSC and ReportCyber',
          'Legal considerations and engaging lawyers',
          'Managing costs and budgets during incident response'
        ]
      },
      {
        title: 'Post-Incident Review and Improvement',
        duration: '1.5 hours',
        topics: [
          'Conducting a post-incident lessons learned review',
          'Identifying security gaps and improvement opportunities',
          'Updating security controls and procedures',
          'Employee re-training after incidents',
          'Documentation and record-keeping requirements',
          'Building resilience for future incidents'
        ]
      },
      {
        title: 'Building Your Incident Response Playbook',
        duration: '0.5 hours',
        topics: [
          'Customising incident response templates for your business',
          'Creating contact lists and escalation procedures',
          'Documenting roles and responsibilities',
          'Testing your playbook through tabletop exercises',
          'Maintaining and updating your incident response plan'
        ]
      }
    ],
    
    format: {
      type: 'Live webinar or self-paced online modules',
      includes: [
        '16 hours of actionable instruction',
        'Customisable incident response playbook templates',
        'Notification letter templates for Australian privacy law compliance',
        'Tabletop exercise scenarios for practice',
        'Contact lists and escalation procedure templates',
        'Post-incident review checklist',
        'Access to recorded sessions for 12 months',
        'Certificate of completion',
        'Email and phone support for 60 days post-course'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Four half-day live sessions (4 hours each) over two weeks, or self-paced access available immediately'
    },
    
    faqs: [
      {
        question: 'What if we don\'t have dedicated IT staff?',
        answer: 'This course is designed for SMBs with limited or no IT staff. You\'ll learn to make critical decisions, coordinate external help, and execute basic containment actions yourself. We cover extensively how to work with external IT providers and managed service providers during incidents.'
      },
      {
        question: 'Do we need technical skills or forensic expertise?',
        answer: 'No forensic expertise required. This course focuses on practical actions SMBs can take—not complex technical analysis. You\'ll learn when to engage external experts and how to work with them effectively.'
      },
      {
        question: 'Will this help us comply with privacy notification requirements?',
        answer: 'Yes, we cover Australian Notifiable Data Breaches (NDB) obligations in detail, including when you must notify, who to notify, notification timeframes, and template letters. You\'ll understand your legal obligations under the Privacy Act and APPs.'
      },
      {
        question: 'What if we\'re currently experiencing an incident?',
        answer: 'If you\'re in an active incident, seek immediate help: contact the ACSC via cyber.gov.au, call 1300 CYBER1, or engage an incident response provider. This course will help you prepare for and respond to future incidents. Some participants take the course after an incident to improve preparedness.'
      },
      {
        question: 'Does this course cover cyber insurance and making claims?',
        answer: 'Yes, we cover working with cyber insurance during incidents including notification requirements, evidence preservation for claims, and coordinating with insurers. We\'ll help you understand what cyber insurance typically covers for SMBs and how to maximise your policy.'
      },
      {
        question: 'How is this different from enterprise incident response training?',
        answer: 'This course is tailored specifically for SMB realities—limited budgets, small teams, reliance on external providers, and practical constraints. We don\'t cover advanced forensics or enterprise-scale security operations. It\'s about what you can actually do with SMB resources.'
      },
      {
        question: 'Will we practice responding to incidents during the course?',
        answer: 'Yes, the course includes tabletop exercises where you\'ll work through realistic SMB incident scenarios step-by-step. You\'ll practise making decisions, executing procedures, and using the templates provided. This builds confidence for real incidents.'
      },
      {
        question: 'Can we customise the playbook for our specific business?',
        answer: 'Absolutely—that\'s a key course outcome. You\'ll receive customisable templates and work through exercises to build a playbook specific to your business, systems, contacts, and operations. By the end, you\'ll have a practical, ready-to-use incident response plan.'
      }
    ]
};
